import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/api';

export function mapProductListItem(p) {
  return {
    id: p.id,
    name: p.name,
    slug: p.slug || '',
    description: p.description || '',
    category: p.category_name || '',
    subCategory: p.subcategory_name || '',
    price: Number(p.price),
    originalPrice: p.original_price ? Number(p.original_price) : null,
    discountPercentage: p.discount_percentage || 0,
    image: p.primary_image || null,
    imageSrcset: p.primary_image_srcset || '',
    rating: p.rating || 0,
    reviews: p.review_count || 0,
    inStock: !!p.in_stock,
    isFeatured: !!p.is_featured,
  };
}

function mapProductDetail(data) {
  const variants = Array.isArray(data.variants) ? data.variants : [];
  const images = Array.isArray(data.images) ? data.images : [];

  const allImages = images
    .map(img => ({
      id: img.id,
      url: img.image_url || img.image,
      altText: img.alt_text || '',
      isPrimary: img.is_primary || false,
      order: img.order || 0,
    }))
    .sort((a, b) => a.order - b.order);

  const primaryImageUrl =
    data.primary_image ||
    allImages.find(img => img.isPrimary)?.url ||
    allImages[0]?.url ||
    null;

  return {
    id: data.id,
    name: data.name,
    slug: data.slug || '',
    sku: data.sku || '',
    description: data.description || '',
    category: data.category?.name || '',
    categoryId: data.category?.id || null,
    subCategory: data.subcategory?.name || '',
    subCategoryId: data.subcategory?.id || null,
    price: Number(data.price),
    originalPrice: data.original_price ? Number(data.original_price) : null,
    discountPercentage: data.discount_percentage || 0,
    material: data.material || '',
    careInstructions: data.care_instructions || '',
    image: primaryImageUrl,
    images: allImages,
    variants: variants
      .filter(v => v.is_active)
      .map(v => ({
        id: v.id,
        size: v.size,
        stockQuantity: v.stock_quantity || 0,
        finalPrice: v.final_price ? Number(v.final_price) : Number(data.price),
        priceAdjustment: v.price_adjustment ? Number(v.price_adjustment) : 0,
      })),
    rating: data.rating || 0,
    reviews: data.review_count || 0,
    inStock: !!data.in_stock,
    isFeatured: !!data.is_featured,
  };
}

export const fetchProducts = createAsyncThunk(
  'dresses/fetchProducts',
  async (params = {}) => {
    const {
      categoryId = null,
      subCategoryId = null,
      minPrice = '',
      maxPrice = '',
      sortBy = '-created_at',
      searchQuery = '',
      inStockOnly = false,
      page = 1,
      pageSize = 12,
      onSale = false,
    } = params;

    const query = new URLSearchParams();
    query.set('is_active', 'true');
    query.set('page', String(page));
    query.set('page_size', String(pageSize));

    if (categoryId) query.set('category', String(categoryId));
    if (subCategoryId) query.set('subcategory', String(subCategoryId));
    if (minPrice !== '' && minPrice !== null) query.set('min_price', String(minPrice));
    if (maxPrice !== '' && maxPrice !== null) query.set('max_price', String(maxPrice));
    if (sortBy) query.set('ordering', sortBy);
    if (searchQuery) query.set('search', searchQuery);
    if (inStockOnly) query.set('in_stock', 'true');
    if (onSale) query.set('on_sale', 'true');

    const doFetch = async () => {
      const res = await apiFetch(`/api/public/products/?${query.toString()}`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const results = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      return {
        products: results.map(mapProductListItem),
        count: data.count ?? results.length,
        next: data.next ?? null,
        previous: data.previous ?? null,
      };
    };

    try {
      return await doFetch();
    } catch (_) {
      // Retry once after 1.5 s to recover from cold-start / transient errors
      await new Promise(r => setTimeout(r, 1500));
      return await doFetch();
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'dresses/fetchCategories',
  async () => {
    // No explicit ordering param — the backend defaults to the marketing
    // team's custom Category Order (falls back to name for ties).
    const doFetch = async () => {
      const res = await apiFetch('/api/public/categories/');
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      return list
        .filter(c => c?.is_active !== false)
        .map(c => ({ id: c.id, name: c.name }));
    };

    try {
      return await doFetch();
    } catch (_) {
      // Retry once after 1.5 s to recover from cold-start / transient errors
      await new Promise(r => setTimeout(r, 1500));
      return await doFetch();
    }
  }
);

export const fetchSubCategories = createAsyncThunk(
  'dresses/fetchSubCategories',
  async () => {
    const doFetch = async () => {
      const res = await apiFetch('/api/public/subcategories/');
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      return list
        .filter(sc => sc?.is_active !== false)
        .map(sc => ({ id: sc.id, name: sc.name, categoryId: sc.category }));
    };

    try {
      return await doFetch();
    } catch (_) {
      // Retry once after 1.5 s to recover from cold-start / transient errors
      await new Promise(r => setTimeout(r, 1500));
      return await doFetch();
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'dresses/fetchProductById',
  async (id) => {
    const res = await apiFetch(`/api/public/products/${id}/`);
    const data = await res.json();
    if (!res.ok) throw new Error(data?.detail || 'Product not found');
    return mapProductDetail(data);
  }
);

// Aliases for backwards compatibility with App.js
export const fetchDresses = fetchProducts;
export const fetchDressById = fetchProductById;

const dressesSlice = createSlice({
  name: 'dresses',
  initialState: {
    items: [],
    selectedDress: null,
    loading: true,
    error: null,
    pagination: { count: 0, next: null, previous: null },
    categories: [],
    categoriesLoading: false,
    subCategories: [],
    subCategoriesLoading: false,
  },
  reducers: {
    clearSelectedDress: (state) => {
      state.selectedDress = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.products;
        state.pagination = {
          count: action.payload.count,
          next: action.payload.next,
          previous: action.payload.previous,
        };
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDress = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.categoriesLoading = false;
      })
      .addCase(fetchSubCategories.pending, (state) => {
        state.subCategoriesLoading = true;
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.subCategoriesLoading = false;
        state.subCategories = action.payload;
      })
      .addCase(fetchSubCategories.rejected, (state) => {
        state.subCategoriesLoading = false;
      });
  },
});

export const { clearSelectedDress, clearError } = dressesSlice.actions;

export const selectAllDresses = (state) => state.dresses.items;
export const selectSelectedDress = (state) => state.dresses.selectedDress;
export const selectDressesLoading = (state) => state.dresses.loading;
export const selectDressesError = (state) => state.dresses.error;
export const selectCategories = (state) => state.dresses.categories;
export const selectCategoriesLoading = (state) => state.dresses.categoriesLoading;
export const selectSubCategories = (state) => state.dresses.subCategories;
export const selectSubCategoriesLoading = (state) => state.dresses.subCategoriesLoading;
export const selectPagination = (state) => state.dresses.pagination;

export default dressesSlice.reducer;
